import { jsPDF } from "jspdf";

// A4 layout copy private to dischargeSummaryPdf (house convention: every
// renderer owns its layout file — no cross-renderer imports). Copied from
// prescriptionLayout.ts 2026-10-16; editing this file must never require
// touching prescriptionLayout.ts or any other layout (and vice versa).

export const PAGE_MARGIN_MM = 10;
export const TEXT_INSET_MM = 10;
export const TEXT_X_MM = 10;
export const TEXT_WIDTH_MM = 190;
export const PAGE_HEIGHT_MM = 297;
export const PAGE_WIDTH_MM = 210;

const HEADER_PATH = "/assets/header.png";
const FOOTER_PATH = "/assets/footer.png";
/**
 * Letterhead area reserved when the PNG is missing/unreadable. The text
 * fallback header draws a box of exactly this height, so the text region
 * stays identical whether the image loaded or not.
 */
const HEADER_FALLBACK_MM = 22;
const FOOTER_FALLBACK_MM = 18;
/**
 * Caps so a near-square letterhead image can never eat the whole page.
 * If the cap bites, the width shrinks proportionally (still aspect-true)
 * and the rect is centered horizontally.
 */
const HEADER_MAX_MM = 80;
const FOOTER_MAX_MM = 40;

const MM_TO_PT = 2.834645669;

export interface TextRegion {
  x: number;
  y: number;
  width: number;
  maxHeight: number;
}

interface LetterheadImage {
  dataUrl: string;
  widthPx: number;
  heightPx: number;
}

const imageCache = new Map<string, LetterheadImage>();

/**
 * Heights (pt) of the letterhead areas as last drawn by drawHeader/drawFooter.
 * getTextRegion() and getSignatureLineY() read these, so body text always
 * starts below the header and stops above the footer whatever the images'
 * aspect ratios are.
 */
let headerHeightPt = mmToPt(HEADER_FALLBACK_MM);
let footerHeightPt = mmToPt(FOOTER_FALLBACK_MM);

async function loadImage(url: string): Promise<LetterheadImage | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
    const blob = await res.blob();
    const dataUrl = await blobToDataURL(blob);
    const size = await measureImage(dataUrl);
    const img = { dataUrl, widthPx: size.width, heightPx: size.height };
    imageCache.set(url, img);
    return img;
  } catch {
    return null;
  }
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Natural pixel size of a data URL — used to keep the letterhead aspect-true. */
function measureImage(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = dataUrl;
  });
}

export function mmToPt(mm: number): number {
  return mm * MM_TO_PT;
}

/**
 * Fit a letterhead image into the full content width (page width minus the
 * two page margins) at its native aspect ratio: height = width / aspect.
 * Height is capped; when the cap bites, width shrinks to keep the ratio
 * exact and the rect is centered in the content width.
 */
function fitLetterhead(
  img: LetterheadImage,
  maxHeightMm: number,
): { x: number; w: number; h: number } {
  const boxW = mmToPt(PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM);
  const maxH = mmToPt(maxHeightMm);
  let w = boxW;
  let h = w * (img.heightPx / img.widthPx);
  if (h > maxH) {
    h = maxH;
    w = h * (img.widthPx / img.heightPx);
  }
  return { x: mmToPt(PAGE_MARGIN_MM) + (boxW - w) / 2, w, h };
}

export async function drawHeader(doc: jsPDF): Promise<void> {
  const y = mmToPt(PAGE_MARGIN_MM);
  const img = await loadImage(HEADER_PATH);
  if (img) {
    const { x, w, h } = fitLetterhead(img, HEADER_MAX_MM);
    try {
      doc.addImage(img.dataUrl, "PNG", x, y, w, h);
      headerHeightPt = h;
      return;
    } catch {
      // fall through to the text fallback below
    }
  }
  headerHeightPt = mmToPt(HEADER_FALLBACK_MM);
  drawTextHeader(
    doc,
    mmToPt(PAGE_MARGIN_MM),
    y,
    mmToPt(PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM),
    headerHeightPt,
  );
}

function drawTextHeader(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setFillColor(247, 240, 232);
  doc.rect(x, y, w, h, "F");
  doc.setTextColor(124, 45, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Clinic EMR", x + mmToPt(5), y + mmToPt(13));
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(107, 95, 88);
  doc.text("Clinic letterhead — place header.png at /assets/header.png", x + mmToPt(5), y + mmToPt(18));
}

export async function drawFooter(doc: jsPDF): Promise<void> {
  const img = await loadImage(FOOTER_PATH);
  if (img) {
    const { x, w, h } = fitLetterhead(img, FOOTER_MAX_MM);
    // Bottom-anchored: the footer's bottom edge sits on the page margin.
    const y = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - h;
    try {
      doc.addImage(img.dataUrl, "PNG", x, y, w, h);
      footerHeightPt = h;
      return;
    } catch {
      // skip
    }
  }
  footerHeightPt = mmToPt(FOOTER_FALLBACK_MM);
}

/**
 * Body-text region: starts 4mm below the header area, ends 4mm above the
 * footer area. Heights are whatever the last drawHeader/drawFooter actually
 * drew, so text never overlaps the letterhead at any aspect ratio.
 */
export function getTextRegion(doc: jsPDF): TextRegion {
  const headerBottom = mmToPt(PAGE_MARGIN_MM) + headerHeightPt;
  const footerTop = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - footerHeightPt;
  const y = headerBottom + mmToPt(4);
  return {
    x: mmToPt(TEXT_X_MM),
    y,
    width: mmToPt(TEXT_WIDTH_MM),
    maxHeight: footerTop - mmToPt(4) - y,
  };
}

/**
 * Y (pt, from the page top) for the signature rule. Anchored above the
 * footer with room for the three signatory text lines drawn below the
 * rule (name +4mm, qualifications +7mm, reg no +10mm) plus 2mm clearance.
 */
export function getSignatureLineY(doc: jsPDF): number {
  const footerTop = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - footerHeightPt;
  return footerTop - mmToPt(12);
}

export async function addPageWithHeader(doc: jsPDF): Promise<number> {
  doc.addPage();
  await drawHeader(doc);
  await drawFooter(doc);
  return getTextRegion(doc).y;
}

export function newPageY(doc: jsPDF, currentY: number, neededHeight: number): Promise<number> {

  return ensureSpace(doc, currentY, neededHeight);
}

export async function ensureSpace(doc: jsPDF, currentY: number, neededHeight: number): Promise<number> {
  const region = getTextRegion(doc);
  const bottomLimit = region.y + region.maxHeight;
  if (currentY + neededHeight > bottomLimit) {
    return addPageWithHeader(doc);
  }
  return currentY;
}
